import { NgModule } from '@angular/core';
import { Route, RouterModule, Routes } from '@angular/router';
import { AuthGuard } from '@classifieds-sell/auth';
import { CategoryEnum, NEW_HOMES_CATEGORIES } from 'libs/datalayer/src';
import { residentialForRentRoutes } from './category-routes/residential-for-rent-routes';
import { residentialForSaleRoutes } from './category-routes/residential-for-sale-routes';
import { residentialForSaleAgentRoutes } from './category-routes/residential-for-sale-agent-routes';
import { lifeStyleRoutes } from './category-routes/lifestyle-routes';
import { PropertyHomeComponent } from './components/property-home/property-home.component';
import { PropertyLandingComponent } from './components/property-landing/property-landing.component';
import { PropertyNotFoundComponent } from './components/property-not-found/property-not-found.component';
import { PropertyUiChildRouteGuard } from './guards/property-ui-child-route.guard';
import { PropertyUiLifeStyleChildRouteGuard } from './guards/property-ui-lifestyle-child-route-guard';
import { PropertyUiRouteGuard } from './guards/property-ui-route.guard';
import { newHomesRoutes } from './category-routes/new-homes-routes';
import { PropertyUiNewHomesChildRouteGuard } from './guards/property-ui-new-homes-child-route-guard';
import { PropertyUiSectionsForSaleChildRouteGuard } from './guards/property-ui-sections-for-sale-child-route-guard';
import { sectionsForSaleRoutes } from './category-routes/sections-for-sale-routes';
import { PropertyUiRuralChildRouteGuard } from './guards/property-ui-rural-child-route-guard';
import { ruralRoutes } from './category-routes/rural-routes';

const getNewHomesRoutes = (): Route[] => {
    const routes: Route[] = [];
    NEW_HOMES_CATEGORIES.forEach((c) => {
        routes.push({
            path: `${c.toString()}/:subCategory/Agent`,
            canActivate: [PropertyUiNewHomesChildRouteGuard],
            children: newHomesRoutes,
            data: {
                categoryId: c,
                agent: true
            },
            component: PropertyHomeComponent
        });
    });
    NEW_HOMES_CATEGORIES.forEach((c) => {
        routes.push({
            path: `${c.toString()}/:subCategory/Agent/relist/:listingId`,
            canActivate: [PropertyUiNewHomesChildRouteGuard],
            children: newHomesRoutes,
            data: {
                categoryId: c,
                agent: true,
                relisting: true
            },
            component: PropertyHomeComponent
        });
    });
    NEW_HOMES_CATEGORIES.forEach((c) => {
        routes.push({
            path: `${c.toString()}/:subCategory/Agent/edit/:listingId`,
            canActivate: [PropertyUiNewHomesChildRouteGuard],
            children: newHomesRoutes,
            data: {
                categoryId: c,
                agent: true,
                editing: true
            },
            component: PropertyHomeComponent
        });
    });

    return routes;
};

// this configuration should be playing along with guards and logic in landing component
export const propertyUiRoutes: Routes = [
    {
        path: '',
        canActivate: [AuthGuard, PropertyUiRouteGuard],
        component: PropertyLandingComponent,
        children: [
            {
                path: `${CategoryEnum.residentialForRent.toString()}/:subCategory/direct/relist/:listingId`,
                canActivate: [PropertyUiChildRouteGuard],
                children: residentialForRentRoutes,
                data: {
                    categoryId: CategoryEnum.residentialForRent,
                    relisting: true
                },
                component: PropertyHomeComponent
            },
            {
                path: `${CategoryEnum.residentialForRent.toString()}/:subCategory/direct/edit/:listingId`,
                canActivate: [PropertyUiChildRouteGuard],
                children: residentialForRentRoutes,
                data: {
                    categoryId: CategoryEnum.residentialForRent,
                    editing: true
                },
                component: PropertyHomeComponent
            },
            {
                path: `${CategoryEnum.residentialForRent.toString()}/:subCategory`,
                canActivate: [PropertyUiChildRouteGuard],
                children: residentialForRentRoutes,
                data: {
                    categoryId: CategoryEnum.residentialForRent
                },
                component: PropertyHomeComponent
            },
            {
                path: `${CategoryEnum.residentialForSale.toString()}/:subCategory`,
                canActivate: [PropertyUiChildRouteGuard],
                children: [
                    {
                        path: 'Agent',
                        children: [
                            {
                                path: '',
                                children: residentialForSaleAgentRoutes,
                                data: {
                                    agent: true
                                },
                                component: PropertyHomeComponent
                            },
                            {
                                path: 'edit/:listingId',
                                children: residentialForSaleAgentRoutes,
                                data: {
                                    agent: true,
                                    editing: true,
                                    categoryId: CategoryEnum.residentialForSale
                                },
                                component: PropertyHomeComponent
                            },
                            {
                                path: 'relist/:listingId',
                                children: residentialForSaleAgentRoutes,
                                data: {
                                    agent: true,
                                    relisting: true,
                                    categoryId: CategoryEnum.residentialForSale
                                },
                                component: PropertyHomeComponent
                            }
                        ]
                    },
                    {
                        path: '',
                        children: residentialForSaleRoutes,
                        component: PropertyHomeComponent
                    },
                    {
                        path: 'direct/edit/:listingId',
                        children: residentialForSaleRoutes,
                        data: {
                            editing: true,
                            agent: false,
                            categoryId: CategoryEnum.residentialForSale
                        },
                        component: PropertyHomeComponent
                    },
                    {
                        path: 'direct/relist/:listingId',
                        children: residentialForSaleRoutes,
                        data: {
                            relisting: true,
                            agent: false,
                            categoryId: CategoryEnum.residentialForSale
                        },
                        component: PropertyHomeComponent
                    }
                ],
                data: {
                    categoryId: CategoryEnum.residentialForSale
                }
            },
            {
                path: `${CategoryEnum.lifeStyle.toString()}/:subCategory`,
                canActivate: [PropertyUiLifeStyleChildRouteGuard],
                children: [
                    {
                        path: 'Agent',
                        children: [
                            {
                                path: '',
                                children: lifeStyleRoutes,
                                data: {
                                    agent: true
                                },
                                component: PropertyHomeComponent
                            },
                            {
                                path: 'edit/:listingId',
                                children: lifeStyleRoutes,
                                data: {
                                    agent: true,
                                    editing: true,
                                    categoryId: CategoryEnum.lifeStyle
                                },
                                component: PropertyHomeComponent
                            },
                            {
                                path: 'relist/:listingId',
                                children: lifeStyleRoutes,
                                data: {
                                    agent: true,
                                    relisting: true,
                                    categoryId: CategoryEnum.lifeStyle
                                },
                                component: PropertyHomeComponent
                            }
                        ]
                    }
                ],
                data: {
                    categoryId: CategoryEnum.lifeStyle
                }
            },
            ...getNewHomesRoutes(),
            {
                path: `${CategoryEnum.sectionsForSale.toString()}/:subCategory/Agent`,
                canActivate: [PropertyUiSectionsForSaleChildRouteGuard],
                children: sectionsForSaleRoutes,
                data: {
                    categoryId: CategoryEnum.sectionsForSale,
                    agent: true
                },
                component: PropertyHomeComponent
            },
            {
                path: `${CategoryEnum.sectionsForSale.toString()}/:subCategory/Agent/edit/:listingId`,
                canActivate: [PropertyUiSectionsForSaleChildRouteGuard],
                children: sectionsForSaleRoutes,
                data: {
                    categoryId: CategoryEnum.sectionsForSale,
                    agent: true,
                    editing: true
                },
                component: PropertyHomeComponent
            },
            {
                path: `${CategoryEnum.sectionsForSale.toString()}/:subCategory/Agent/relist/:listingId`,
                canActivate: [PropertyUiSectionsForSaleChildRouteGuard],
                children: sectionsForSaleRoutes,
                data: {
                    categoryId: CategoryEnum.sectionsForSale,
                    agent: true,
                    relisting: true
                },
                component: PropertyHomeComponent
            },
            {
                path: `${CategoryEnum.rural.toString()}/:subCategory/Agent`,
                canActivate: [PropertyUiRuralChildRouteGuard],
                children: ruralRoutes,
                data: {
                    categoryId: CategoryEnum.rural,
                    agent: true
                },
                component: PropertyHomeComponent
            },
            {
                path: `${CategoryEnum.rural.toString()}/:subCategory/Agent/edit/:listingId`,
                canActivate: [PropertyUiRuralChildRouteGuard],
                children: ruralRoutes,
                data: {
                    categoryId: CategoryEnum.rural,
                    agent: true,
                    editing: true
                },
                component: PropertyHomeComponent
            },
            {
                path: `${CategoryEnum.rural.toString()}/:subCategory/Agent/relist/:listingId`,
                canActivate: [PropertyUiRuralChildRouteGuard],
                children: ruralRoutes,
                data: {
                    categoryId: CategoryEnum.rural,
                    agent: true,
                    relisting: true
                },
                component: PropertyHomeComponent
            },
            {
                path: '**',
                component: PropertyNotFoundComponent
            } // this is needed due to design of our parents and children routes and router-outlets
        ]
    },
    {
        path: '**',
        component: PropertyNotFoundComponent
    }
];

@NgModule({
    imports: [RouterModule.forChild(propertyUiRoutes)],
    exports: [RouterModule],
    providers: []
})
export class ListStepsRoutingModule { }
